import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useContext } from 'react';
import {getProducts, getProductGroups, editProductAddOn, editProductGroupAddOn} from "../../api/api";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import { Button, Modal, ModalBody, ModalHeader, ModalFooter, Col, Row } from "reactstrap";
import Avatar from "react-avatar";
import { Link } from "react-router-dom";
import Loader from "../../components/Loader";

const EditCart = ({ isOpen, toggle, cartItem, productAddOnId, isProductGroup, existingAddOns }) => {
    const { refreshToken } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const [selectedAddOns, setSelectedAddOns] = useState(existingAddOns.map(addOn => addOn.id));
    const [supportedAddOns, setSupportedAddOns] = useState([]);
    const [itemName, setItemName] = useState("");
    const page = 1;

    // Determine the API call based on whether the item is a product group
    const fetchAddOns = isProductGroup ? getProductGroups : getProducts;
    const queryKey = isProductGroup ? 'productGroupDetails' : 'productDetails';

    const { data } = useQuery(
        [queryKey, cartItem.id],
        () => fetchAddOns(page, cartItem.type === 'PRODUCT' ? cartItem.product_add_on_item.product.id : cartItem.product_group_add_on_item.product_group.id),
        {
            keepPreviousData: true,
            onSuccess: (data) => {
                console.log("pg", data.results[0].add_ons_supported.add_on_products)
                if (data.results && data.results.length > 0) {
                    const item = data.results[0];
                    if (isProductGroup && item.add_ons_supported) {
                        const uniqueAddOns = item.add_ons_supported.add_on_products
                            ?.filter((value, index, self) =>
                                    index === self.findIndex((t) => (
                                        t.id === value.id
                                    ))
                            );
                        setSupportedAddOns(uniqueAddOns || []);
                        setItemName(item.name);
                    } else if (!isProductGroup) {
                        const product = item;
                        if (product.add_ons_supported) {
                            setSupportedAddOns(product.add_ons_supported.add_on_products || []);
                            setItemName(product.name);
                        }
                    }
                }
            },
            onError: (err) => {
                handleUnauthorized401QueryError(err, data, refreshToken, () => {});
                if (err.response.status !== 401) {
                    toast.error(`An error occurred! ${err?.response?.data?.detail}`);
                }
            },
        }
    );

    const editAddonMutation = useMutation(
        (addOnIds) => {
            const mutationFn = isProductGroup
                ? editProductGroupAddOn
                : editProductAddOn;

            return mutationFn({ add_on_products: addOnIds }, productAddOnId);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cart']);
                if (!toast.isActive('addonAddedToast')) {
                    toast.success('Add-Ons Updated Successfully!', { toastId: 'addonAddedToast' });
                }
                toggle(); // Close the modal after saving
            },
            onError: (err) => {
                handleUnauthorized401MutateError(err, refreshToken, editAddonMutation);
                toast.error('Failed to update add-ons.');
            }
        }
    );

    const handleAddOnClick = (addOnProduct) => {
        setSelectedAddOns(prevSelected => {
            if (prevSelected.includes(addOnProduct.id)) {
                return prevSelected.filter(id => id !== addOnProduct.id);
            } else {
                return [...prevSelected, addOnProduct.id];
            }
        });
    };

    const handleEditAddOnSave = () => {
        editAddonMutation.mutate(selectedAddOns);
    };

    console.log("supported", supportedAddOns)

    return (
        <Modal className={'modal-lg'} isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle} close={
                <button className="close" onClick={toggle}>
                    &times;
                    </button>
            }>Edit Add-Ons {itemName}</ModalHeader>
            <ModalBody>
                <Row>
                    {supportedAddOns.length > 0 ? (
                        supportedAddOns.map((addOn, index) => (
                            <Col lg="4" key={addOn.id || index}>
                                <Link>
                                    <div
                                        className={`card ribbon-box ${selectedAddOns.includes(addOn.id) ? 'border border-primary' : ''} ${addOn.stock === 0 ? 'out-of-stock' : ''}`}
                                        style={{ background: "#202027", pointerEvents: addOn.stock === 0 ? 'none' : 'auto' }}
                                        onClick={() => handleAddOnClick(addOn)}
                                    >
                                        {addOn.stock === 0 && (
                                            <div className="ribbon-two ribbon-two-danger">
                                                <span className="stock-banner">Out of Stock</span>
                                            </div>
                                        )}
                                        <div className="box-details">
                                        {addOn.image ? (
                                            <img src={addOn.image} className="card-img-top img-fluid box-image" alt="product" />
                                        ) : (
                                            <Avatar name={addOn.name} size="240" round={false} className="addon-avatar"/>
                                        )}
                                        </div>
                                        <div className="card-body" style={{ background: "#202027" }}>
                                            <h5 className="card-title" data-toggle="tooltip" data-placement="top" title={addOn.name}>{addOn.name}</h5>
                                            <p className="card-text">INR {addOn.price}</p>
                                        </div>
                                    </div>
                                </Link>
                            </Col>
                        ))
                    ) : (
                        <Col>
                            <p>No additional add-ons available for this item.</p>
                        </Col>
                    )}
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleEditAddOnSave} disabled={editAddonMutation.isLoading}>
                {editAddonMutation.isLoading? <><Loader size={'small'}/>Saving...</>: `Save`} 
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditCart;
